import axios from "axios";
import apiClient from "./api";

export interface RegisterRequest {
  email: string;
  password: string;
  companyName?: string;
  companyDescription?: string;
  website?: string;
}

export interface LoginRequest {
  email: string;
  password: string;
}

export interface Company {
  id: string;
  name: string;
  description?: string;
  address?: string;
  phone?: string;
  email?: string;
  website?: string;
  createdAt: string;
  updatedAt: string;
  isActive: boolean;
  userCount: number;
  /** Presence flags only — the keys themselves never leave the server. */
  hasOpenAiKey?: boolean;
  hasGeminiKey?: boolean;
  onboardingCompleted: boolean;
  onboardingState?: string;
  features?: string[];
  require2FA?: boolean;
}

export interface User {
  id: string;
  email: string;
  role: string;
  companyId: string;
  companyName: string;
  company?: Company;
}

export interface AuthResponse {
  token: string;
  refreshToken?: string;
  expiresIn?: number;
  user: User;
  company: Company;
  // Two-factor login challenge: when mfaRequired is true, token/user/company are absent and the
  // client must complete the second factor (verify or enroll) using mfaToken.
  mfaRequired?: boolean;
  mfaToken?: string;
  setupRequired?: boolean;
  // Self-service signup with email verification on: nothing is created yet and token is empty.
  // The account is created when the emailed link is opened (verifyEmail).
  emailVerificationRequired?: boolean;
}

export interface TwoFactorSetup {
  secret: string;
  otpauthUri: string;
}

export interface TwoFactorStatus {
  enabled: boolean;
  companyRequires: boolean;
}

export interface RefreshTokenRequest {
  refreshToken: string;
  accessToken?: string;
}

export interface AcceptInvitationRequest {
  token: string;
  password: string;
}

/**
 * Persist a successful auth response to localStorage. No-ops when there is no token
 * (e.g. a 2FA challenge response), so the caller can branch on `mfaRequired`.
 */
const persistAuth = (authData: AuthResponse): void => {
  if (!authData.token) return;
  localStorage.setItem("accessToken", authData.token);
  if (authData.refreshToken) {
    localStorage.setItem("refreshToken", authData.refreshToken);
  }
  if (authData.expiresIn) {
    const expiryTime = Date.now() + authData.expiresIn * 1000;
    localStorage.setItem("tokenExpiry", expiryTime.toString());
  }
  const userWithCompany = { ...authData.user, company: authData.company };
  localStorage.setItem("user", JSON.stringify(userWithCompany));
};

/** A JWT's payload, unchecked: only trust it once the API has accepted the same token. */
const readTokenPayload = (token: string): Record<string, unknown> | null => {
  try {
    const payload = token.split(".")[1];
    return payload
      ? JSON.parse(atob(payload.replace(/-/g, "+").replace(/_/g, "/")))
      : null;
  } catch {
    return null;
  }
};

let refreshInFlight: Promise<AuthResponse | null> | null = null;

export const authService = {
  register: async (data: RegisterRequest): Promise<AuthResponse> => {
    const response = await apiClient.post("/auth/register", data);
    const authData = response.data;

    persistAuth(authData);
    return authData;
  },

  login: async (data: LoginRequest): Promise<AuthResponse> => {
    const response = await apiClient.post("/auth/login", data);
    const authData = response.data;

    persistAuth(authData);
    return authData;
  },

  /**
   * Exchanges the refresh token for a new access token. Single-flight: callers that overlap
   * (the expiry timer and the chat widget asking for a fresh token) share one request, so one
   * refresh token is never spent twice.
   */
  refreshToken: (): Promise<AuthResponse | null> => {
    if (refreshInFlight) return refreshInFlight;
    refreshInFlight = (async () => {
      try {
        const refreshToken = localStorage.getItem("refreshToken");
        if (!refreshToken) {
          return null;
        }

        const accessToken = localStorage.getItem("accessToken");
        const response = await apiClient.post("/auth/refresh", {
          refreshToken,
          accessToken,
        });
        const authData = response.data;

        persistAuth(authData);
        return authData;
      } catch (error) {
        console.error("Token refresh failed:", error);
        authService.logout();
        return null;
      } finally {
        refreshInFlight = null;
      }
    })();
    return refreshInFlight;
  },

  logout: () => {
    window.StrikeUpChat?.destroy();
    const refreshToken = localStorage.getItem("refreshToken");
    const accessToken = localStorage.getItem("accessToken");
    if (refreshToken) {
      apiClient
        .post(
          "/auth/logout",
          { refreshToken },
          accessToken
            ? { headers: { Authorization: `Bearer ${accessToken}` } }
            : undefined,
        )
        .catch(() => {});
    }
    localStorage.removeItem("accessToken");
    localStorage.removeItem("refreshToken");
    localStorage.removeItem("tokenExpiry");
    localStorage.removeItem("user");
    // Remove old token key for backward compatibility
    localStorage.removeItem("authToken");
  },

  getCurrentUser: (): User | null => {
    const userStr = localStorage.getItem("user");
    return userStr ? JSON.parse(userStr) : null;
  },

  getAccessToken: (): string | null => {
    return localStorage.getItem("accessToken");
  },

  getRefreshToken: (): string | null => {
    return localStorage.getItem("refreshToken");
  },

  isTokenExpired: (): boolean => {
    const expiryStr = localStorage.getItem("tokenExpiry");
    if (!expiryStr) return true;

    const expiry = parseInt(expiryStr, 10);
    // Consider token expired 30 seconds before actual expiry for safety
    return Date.now() >= expiry - 30000;
  },

  isAuthenticated: (): boolean => {
    const hasToken = !!localStorage.getItem("accessToken");
    if (!hasToken) return false;

    // Check if token is expired
    if (authService.isTokenExpired()) {
      return false;
    }

    return true;
  },

  fetchAndStoreCurrentUser: async (): Promise<void> => {
    const response = await apiClient.get<{ user: User; company: Company }>(
      "/auth/me",
    );
    const { user, company } = response.data;
    localStorage.setItem("user", JSON.stringify({ ...user, company }));
  },

  /**
   * Starts a session from a super-admin impersonation link (SEC-26). The token replaces the stored
   * session only if the API accepts it and it carries the ImpersonatedBy claim, which only the
   * super-admin impersonate endpoint issues; an ordinary sign-in token put in a link is refused and
   * the current session is left as it was. The call bypasses apiClient so the stored token is not
   * sent instead and a 401 does not trigger a refresh or sign-out of the current session.
   */
  startImpersonation: async (token: string): Promise<boolean> => {
    const claims = readTokenPayload(token);
    const exp = claims?.exp;
    if (!claims?.ImpersonatedBy || typeof exp !== "number") return false;

    try {
      const response = await axios.get<{ user: User; company: Company }>(
        `${apiClient.defaults.baseURL}/auth/me`,
        { headers: { Authorization: `Bearer ${token}` } },
      );
      const { user, company } = response.data;
      localStorage.setItem("accessToken", token);
      localStorage.setItem("tokenExpiry", (exp * 1000).toString());
      localStorage.removeItem("refreshToken");
      localStorage.setItem("user", JSON.stringify({ ...user, company }));
      return true;
    } catch {
      return false;
    }
  },

  isAdmin: (): boolean => {
    const user = authService.getCurrentUser();
    return user?.role === "Admin";
  },

  changePassword: async (
    currentPassword: string,
    newPassword: string,
  ): Promise<void> => {
    await apiClient.post("/auth/change-password", {
      currentPassword,
      newPassword,
      refreshToken: localStorage.getItem("refreshToken"),
    });
  },

  forgotPassword: async (email: string): Promise<void> => {
    await apiClient.post("/auth/forgot-password", { email });
  },

  resetPassword: async (token: string, newPassword: string): Promise<void> => {
    await apiClient.post("/auth/reset-password", { token, newPassword });
  },

  acceptInvitation: async (
    data: AcceptInvitationRequest,
  ): Promise<AuthResponse> => {
    const response = await apiClient.post("/auth/accept-invitation", data);
    const authData = response.data;

    persistAuth(authData);
    return authData;
  },

  /** Redeems the signup confirmation link: creates the account and signs the user in. */
  verifyEmail: async (token: string): Promise<AuthResponse> => {
    const response = await apiClient.post("/auth/verify-email", { token });
    const authData = response.data;

    persistAuth(authData);
    return authData;
  },

  // ── Two-factor authentication ──────────────────────────────────────────────

  // Login-time completion (anonymous; authorised by the short-lived mfaToken).
  verify2faLogin: async (
    mfaToken: string,
    code: string,
  ): Promise<AuthResponse> => {
    const response = await apiClient.post("/auth/2fa/login-verify", {
      mfaToken,
      code,
    });
    persistAuth(response.data);
    return response.data;
  },

  start2faLoginSetup: async (mfaToken: string): Promise<TwoFactorSetup> => {
    const response = await apiClient.post("/auth/2fa/login-setup/start", {
      mfaToken,
    });
    return response.data;
  },

  complete2faLoginSetup: async (
    mfaToken: string,
    code: string,
  ): Promise<AuthResponse> => {
    const response = await apiClient.post("/auth/2fa/login-setup/complete", {
      mfaToken,
      code,
    });
    persistAuth(response.data);
    return response.data;
  },

  // Self-service management (authenticated).
  get2faStatus: async (): Promise<TwoFactorStatus> => {
    const response = await apiClient.get("/auth/2fa/status");
    return response.data;
  },

  setup2fa: async (): Promise<TwoFactorSetup> => {
    const response = await apiClient.post("/auth/2fa/setup");
    return response.data;
  },

  enable2fa: async (code: string): Promise<void> => {
    await apiClient.post("/auth/2fa/enable", { code }, { __silent: true });
  },

  disable2fa: async (code: string): Promise<void> => {
    await apiClient.post("/auth/2fa/disable", { code }, { __silent: true });
  },
};
